import { Mixpanel } from './Mixpanel'

/**
 * Global Content-Security-Policy violation reporter.
 *
 * When the browser blocks something the CSP forbids (a cross-origin script, an
 * inline handler, or an `eval`/`new Function` under a policy without
 * `'unsafe-eval'`) it fires a `securitypolicyviolation` event. Without a listener
 * that signal only lands in the devtools console — which is exactly how the
 * "Sync Failed" incident stayed invisible in prod: the seed decoder's protobufjs
 * codegen tripped `script-src` (no `'unsafe-eval'`) and nothing surfaced it.
 *
 * This wires those events to Mixpanel so future CSP regressions are observable.
 * Violations are deduped by a stable signature and the total is capped, so a
 * flood of identical blocks (e.g. many disallowed image loads) can't spam
 * analytics. CSP only applies in production-mode builds (see server/index.ts),
 * so in dev this simply never fires.
 */

// Cap distinct reports per page load — a runaway policy shouldn't flood analytics.
const MAX_REPORTS = 25

const reportedSignatures = new Set<string>()
let reportCount = 0
let registered = false

/** Bound the size of any single free-form field sent to analytics. */
const truncate = (value: string | undefined | null, max: number): string =>
  value ? String(value).slice(0, max) : ''

const handleViolation = (event: SecurityPolicyViolationEvent): void => {
  // A stable identity for this violation so N identical ones report once.
  const signature = [
    event.effectiveDirective || event.violatedDirective,
    event.blockedURI,
    event.sourceFile,
    event.lineNumber,
    event.columnNumber
  ].join('|')

  if (reportedSignatures.has(signature) || reportCount >= MAX_REPORTS) {
    return
  }
  reportedSignatures.add(signature)
  reportCount += 1

  const properties = {
    violated_directive: event.violatedDirective,
    effective_directive: event.effectiveDirective,
    blocked_uri: truncate(event.blockedURI, 200),
    source_file: truncate(event.sourceFile, 200),
    line_number: event.lineNumber,
    column_number: event.columnNumber,
    disposition: event.disposition,
    status_code: event.statusCode,
    // `sample` is a short snippet of the offending markup/script when available.
    sample: truncate(event.sample, 100),
    page_path: typeof window !== 'undefined' ? truncate(window.location?.pathname, 200) : ''
  }

  // Keep console visibility (matches how the seed logged its failure) and report.
  console.error('[csp] violation', properties)
  Mixpanel.track('csp_violation', properties)
}

/**
 * Attach the global CSP-violation listener. Idempotent and a no-op outside the
 * browser, so it is safe to call once from the client bootstrap.
 */
export const registerCspViolationReporter = (): void => {
  if (registered || typeof document === 'undefined') {
    return
  }
  registered = true
  document.addEventListener('securitypolicyviolation', handleViolation)
}
